import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';

import { PolipayPaymentsRepository } from '@app/domain/ports/polipay-payments.repository';
import {
  EncryptionKeyResponse,
  PaymentChargeCreateRequest,
  PaymentChargeListParams,
  PaymentChargeListResponse,
  PaymentChargeListRow,
  PaymentChargeResponse,
  PaymentCompany,
  PaymentCompanyCreateRequest,
  PaymentCompanyListParams,
  PaymentCompanyListResponse,
  PaymentCompanySummary,
  PaymentLinkCreateRequest,
  PaymentLinkListParams,
  PaymentLinkListResponse,
  PaymentLinkResponse,
  PaymentLinkSendRequest,
  PaymentLinkSendResponse,
  PaymentLinkStatus,
  PaymentSummaryGlobalResponse,
  PaymentSummaryQueryParams,
  PaymentSummaryResponse,
  SummaryBucket,
} from '@app/shared/models/polipay-payment.model';

const MOCK_DELAY_MS = 300;

/** Códigos de relleno del alta de comercio: simulan el rechazo del segundo factor. */
const MOCK_TOTP_INVALIDO = '000000';
const MOCK_TOTP_SIN_ENROLAR = '999999';
/** `external_ref` fijo del comercio que devuelve el alta simulada. */
const MOCK_EXTERNAL_REF_ALTA = '00000000-0000-4000-8000-000000000044';

/**
 * Un `HttpErrorResponse` con el cuerpo del back, emitido tras la misma latencia que
 * las respuestas buenas (`delay` no retrasa errores: por eso el `timer`).
 */
function mockHttpError(status: number, body: Record<string, string>): Observable<never> {
  return timer(MOCK_DELAY_MS).pipe(
    switchMap(() =>
      throwError(() => new HttpErrorResponse({ status, error: body, url: '/api/v1/payments/companies/' })),
    ),
  );
}

/**
 * Links de demo del listado. 8 filas con diversidad de estados, comercios,
 * creadores y `deleted_at` para ejercitar TODOS los filtros server-side del
 * contrato §Filtros de /links/:
 *  - 4 `Pending` (dos con vencimiento cercano, uno con `paid_after_cancel: false`).
 *  - 2 `Completed` (con `paid_at` no null, uno de ellos con `paid_after_cancel: true`
 *    para probar la ALERTA de devolución del contrato §Cambios 2026-07-21).
 *  - 1 `Expired` (con `expires_at` en el pasado).
 *  - 1 `Cancelled`.
 *  - 1 fila (`id_link: 12`) con `deleted_at != null` para probar `include_deleted`.
 *  - 2 comercios distintos (`id_company: 1` y `2`), acordes al `MOCK_COMPANIES` de
 *    arriba.
 *  - 3 orígenes de creación distintos (`created_by_label`): dos operadores humanos
 *    y una API key.
 */
const MOCK_LINKS: PaymentLinkResponse[] = [
  {
    id_link: 10,
    id: 'mock-link-10',
    url: 'https://links.polipay.mx/mock-10',
    amount: '1250.00',
    currency: 'MXN',
    description: 'Pedido #1234',
    status: 'Pending',
    id_company: 1,
    expires_at: '2027-01-15T23:59:59Z',
    created_at: '2026-09-20T10:15:00Z',
    deleted_at: null,
    company_name: 'Tienda Demo SA',
    created_by_label: 'ana@polipay.io',
  },
  {
    id_link: 11,
    id: 'mock-link-11',
    url: 'https://links.polipay.mx/mock-11',
    amount: '4899.50',
    currency: 'MXN',
    description: 'Reserva hotel Cancún',
    status: 'Pending',
    id_company: 2,
    // Vence pronto (30 días).
    expires_at: '2026-10-25T23:59:59Z',
    created_at: '2026-09-19T14:30:00Z',
    deleted_at: null,
    company_name: 'Franquicia Norte',
    created_by_label: 'apikey:integracion-web',
  },
  {
    id_link: 12,
    id: 'mock-link-12',
    url: 'https://links.polipay.mx/mock-12',
    amount: '320.00',
    currency: 'MXN',
    description: 'Suscripción marzo',
    status: 'Pending',
    id_company: 1,
    expires_at: '2027-02-01T23:59:59Z',
    created_at: '2026-09-18T09:00:00Z',
    // Soft-deleted: solo aparece con include_deleted=true.
    deleted_at: '2026-09-21T08:12:00Z',
    company_name: 'Tienda Demo SA',
    created_by_label: 'juan@ejemplo.com',
  },
  {
    id_link: 13,
    id: 'mock-link-13',
    url: 'https://links.polipay.mx/mock-13',
    amount: '175.00',
    currency: 'MXN',
    description: 'Boleto evento',
    status: 'Pending',
    id_company: 2,
    expires_at: '2026-10-30T23:59:59Z',
    created_at: '2026-09-17T16:45:00Z',
    deleted_at: null,
    company_name: 'Franquicia Norte',
    created_by_label: 'ana@polipay.io',
  },
  {
    id_link: 14,
    id: 'mock-link-14',
    url: 'https://links.polipay.mx/mock-14',
    amount: '890.00',
    currency: 'MXN',
    description: 'Consulta profesional',
    status: 'Completed',
    id_company: 1,
    expires_at: '2027-01-05T23:59:59Z',
    created_at: '2026-09-15T11:20:00Z',
    paid_at: '2026-09-16T08:47:00Z',
    deleted_at: null,
    company_name: 'Tienda Demo SA',
    created_by_label: 'juan@ejemplo.com',
  },
  {
    id_link: 15,
    id: 'mock-link-15',
    url: 'https://links.polipay.mx/mock-15',
    amount: '2400.00',
    currency: 'MXN',
    description: 'Servicio mensual',
    status: 'Completed',
    id_company: 2,
    expires_at: '2026-12-31T23:59:59Z',
    created_at: '2026-09-10T13:00:00Z',
    paid_at: '2026-09-12T15:23:00Z',
    deleted_at: null,
    company_name: 'Franquicia Norte',
    created_by_label: 'apikey:integracion-web',
  },
  {
    id_link: 16,
    id: 'mock-link-16',
    url: 'https://links.polipay.mx/mock-16',
    amount: '560.00',
    currency: 'MXN',
    description: 'Anticipo trabajo',
    // Expired: expires_at en el pasado.
    status: 'Expired',
    id_company: 1,
    expires_at: '2026-08-01T23:59:59Z',
    created_at: '2026-07-20T09:15:00Z',
    deleted_at: null,
    company_name: 'Tienda Demo SA',
    created_by_label: 'ana@polipay.io',
  },
  {
    id_link: 17,
    id: 'mock-link-17',
    url: 'https://links.polipay.mx/mock-17',
    amount: '3200.00',
    currency: 'MXN',
    description: 'Compra mayoreo',
    status: 'Cancelled',
    id_company: 2,
    expires_at: '2027-01-10T23:59:59Z',
    created_at: '2026-09-14T12:00:00Z',
    // El cliente pagó DESPUÉS de cancelar: alerta de devolución del contrato §2026-07-21.
    paid_after_cancel: true,
    paid_at: '2026-09-14T18:30:00Z',
    deleted_at: null,
    company_name: 'Franquicia Norte',
    created_by_label: 'juan@ejemplo.com',
  },
];

/**
 * Comercios de demo del mock. 8 filas: las 5 primeras ejercitan la paginación
 * server-side (`limit=2, offset=2` → filas 3 y 4) y los tres campos del contrato
 * §Companies rev. 2026-09-25 (`legal_name`, `org_company_id`, `external_ref`),
 * con una fila con `legal_name: undefined` para probar el fallback de "—" en la
 * UI. Las 3 últimas (ids 6–8) existen para el campo "Comercio" del cobro QR
 * (RS24): un nombre repetido con el 1 que cae a `external_ref`, un inactivo con
 * cuenta propia y uno con cuenta propia pero sin ningún identificador (pasa el
 * filtro del servidor y lo excluye el cliente). Van al FINAL para no mover la
 * paginación ya documentada.
 */
const MOCK_COMPANIES: PaymentCompany[] = [
  {
    id_company: 1,
    company_name: 'Tienda Demo SA',
    legal_name: 'Tienda Demostrativa SA de CV',
    org_company_id: 101,
    external_ref: 'ext-uuid-1',
    cobra_a: 'cuenta propia',
    gateway_account_id: 'gw_acct_1',
    is_active: true,
  },
  {
    id_company: 2,
    company_name: 'Franquicia Norte',
    legal_name: 'Franquicia Norte SAPI',
    org_company_id: 102,
    external_ref: 'ext-uuid-2',
    cobra_a: 'Tienda Demo SA',
    gateway_account_id: null,
    is_active: true,
  },
  {
    id_company: 3,
    company_name: 'Comercio Sin Configurar',
    legal_name: undefined,
    org_company_id: null,
    external_ref: 'ext-uuid-3',
    cobra_a: 'SIN CONFIGURAR',
    gateway_account_id: null,
    is_active: true,
  },
  {
    id_company: 4,
    company_name: 'Sucursal Sur',
    legal_name: 'Sucursal Sur SA de CV',
    org_company_id: 103,
    external_ref: 'ext-uuid-4',
    cobra_a: 'comercio Memphis',
    gateway_account_id: 'gw_acct_4',
    is_active: false,
  },
  {
    id_company: 5,
    company_name: 'Café del Bajío',
    legal_name: 'Café del Bajío S de RL',
    org_company_id: 104,
    external_ref: 'ext-uuid-5',
    cobra_a: 'cuenta propia',
    gateway_account_id: 'gw_acct_5',
    is_active: true,
  },
  {
    id_company: 6,
    company_name: 'Tienda Demo SA',
    legal_name: 'Tienda Demo Norte SA de CV',
    org_company_id: null,
    external_ref: 'ext-uuid-6',
    cobra_a: 'cuenta propia',
    gateway_account_id: 'gw_acct_6',
    is_active: true,
  },
  {
    id_company: 7,
    company_name: 'Comercio QR Inactivo',
    legal_name: 'Comercio QR Inactivo SA',
    org_company_id: 107,
    external_ref: 'ext-uuid-7',
    cobra_a: 'cuenta propia',
    gateway_account_id: 'gw_acct_7',
    is_active: false,
  },
  {
    id_company: 8,
    company_name: 'Comercio Sin Identificador',
    legal_name: undefined,
    org_company_id: null,
    external_ref: undefined,
    cobra_a: 'cuenta propia',
    gateway_account_id: 'gw_acct_8',
    is_active: true,
  },
];

/**
 * Data source mock de Polipay Payments para desarrollo/testing sin backend.
 * Solo se inyecta cuando `environment.useMocks === true`; en staging/prod la DI
 * resuelve al `PolipayPaymentsRemoteDataSource`. Simula latencia realista.
 */
@Injectable()
export class PolipayPaymentsMockDataSource extends PolipayPaymentsRepository {
  createLink(input: PaymentLinkCreateRequest): Observable<PaymentLinkResponse> {
    return of<PaymentLinkResponse>({
      id_link: 1,
      id: 'mock-link-uuid',
      url: 'https://example.test/pay/mock-link',
      amount: input.amount,
      currency: 'MXN',
      description: input.description,
      status: 'Pending',
      id_company: input.id_company,
      expiresAt: input.expires_at,
    }).pipe(delay(MOCK_DELAY_MS));
  }

  /**
   * Espejo del `listCharges` mock: aplica los filtros del contrato §Filtros de
   * /links/ (rev. 2026-09-25) contra `MOCK_LINKS` en el mismo orden que el
   * backend real:
   *  - `include_deleted` (default `false`): oculta filas con `deleted_at != null`.
   *    Si es `true`, no se descarta nada por ese campo.
   *  - `search`: substring case-insensitive contra `id_link`, `id`, `description`
   *    y `created_by_label`.
   *  - `status`: igualdad estricta con el valor del contrato (`Pending`,
   *    `Completed`, `Expired`, `Cancelled` o el pseudo-valor `Deleted` — este
   *    último devuelve SOLO las filas con `deleted_at != null`).
   *  - `id_company`: igualdad estricta.
   *  - `date_from` / `date_to`: rango inclusivo sobre `created_at` (fecha
   *    `YYYY-MM-DD`); `date_to` cubre el día entero.
   *  - `min_amount` / `max_amount`: rango inclusivo sobre `amount` (decimales).
   *  - Paginación server-side: si viene `limit`, se recorta con
   *    `slice(offset, offset+limit)`. `count` = total ANTES de paginar.
   */
  listLinks(params: PaymentLinkListParams = {}): Observable<PaymentLinkListResponse> {
    let rows: PaymentLinkResponse[] = MOCK_LINKS.slice();

    // El pseudo-valor `status: 'Deleted'` (§Cambios Payments 2026-07-21) devuelve
    // SOLO los soft-deleted; en ese caso ignoramos el default de `include_deleted`.
    const pideSoloBorrados = params.status === 'Deleted';
    if (pideSoloBorrados) {
      rows = rows.filter((r) => r.deleted_at !== null && r.deleted_at !== undefined);
    } else if (params.include_deleted !== true) {
      // Default: solo visibles (deleted_at === null / undefined).
      rows = rows.filter((r) => r.deleted_at === null || r.deleted_at === undefined);
    }

    // Búsqueda case-insensitive contra id, id_link, description y created_by_label.
    if (params.search && params.search.trim()) {
      const term = params.search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          String(r.id_link).includes(term) ||
          (r.id ?? '').toLowerCase().includes(term) ||
          (r.description ?? '').toLowerCase().includes(term) ||
          (r.created_by_label ?? '').toLowerCase().includes(term),
      );
    }

    // Filtro por estado real (Pending/Completed/Expired/Cancelled). El pseudo-valor
    // 'Deleted' ya se resolvió arriba.
    if (params.status && !pideSoloBorrados) {
      rows = rows.filter((r) => r.status === params.status);
    }

    // Igualdad estricta contra id_company (acepta number o string, como el modelo).
    if (params.id_company !== undefined && params.id_company !== null) {
      rows = rows.filter((r) => r.id_company === params.id_company);
    }

    // Rango de fechas sobre created_at. date_to inclusivo del día completo:
    // aceptamos formato `YYYY-MM-DD` y comparamos por concatenación de ISO.
    if (params.date_from) {
      const desde = params.date_from;
      rows = rows.filter((r) => (r.created_at ?? '') >= desde);
    }
    if (params.date_to) {
      const hasta = params.date_to + 'T23:59:59Z';
      rows = rows.filter((r) => (r.created_at ?? '') <= hasta);
    }

    // Rango de monto. amount es string en el modelo; Number() para comparar.
    if (params.min_amount) {
      const min = Number(params.min_amount);
      if (Number.isFinite(min)) rows = rows.filter((r) => Number(r.amount) >= min);
    }
    if (params.max_amount) {
      const max = Number(params.max_amount);
      if (Number.isFinite(max)) rows = rows.filter((r) => Number(r.amount) <= max);
    }

    // Total ANTES de paginar (el backend devuelve `count` del universo filtrado).
    const total = rows.length;

    // Paginación server-side: solo si viene `limit` explícito.
    if (typeof params.limit === 'number' && Number.isFinite(params.limit) && params.limit > 0) {
      const limit = Math.max(1, Math.trunc(params.limit));
      const offset =
        typeof params.offset === 'number' && Number.isFinite(params.offset) && params.offset > 0
          ? Math.trunc(params.offset)
          : 0;
      rows = rows.slice(offset, offset + limit);
    }

    return of<PaymentLinkListResponse>({ count: total, results: rows }).pipe(
      delay(MOCK_DELAY_MS),
    );
  }

  sendLink(_idLink: number, input: PaymentLinkSendRequest): Observable<PaymentLinkSendResponse> {
    return of<PaymentLinkSendResponse>({
      detail: 'mock: link enviado',
      channel: input.channel,
      destination: input.destination,
    }).pipe(delay(MOCK_DELAY_MS));
  }

  cancelLink(idLink: number): Observable<PaymentLinkResponse> {
    return of<PaymentLinkResponse>({
      id_link: idLink,
      id: 'mock-link-uuid',
      url: 'https://example.test/pay/mock-link',
      amount: '0.00',
      currency: 'MXN',
      status: 'Cancelled',
    }).pipe(delay(MOCK_DELAY_MS));
  }

  deleteLink(_idLink: number): Observable<void> {
    return of<void>(undefined).pipe(delay(MOCK_DELAY_MS));
  }

  getSummary(params: PaymentSummaryQueryParams = {}): Observable<PaymentSummaryResponse> {
    // También trae renglones que NO son cobro (R15): la pantalla los tiene que separar.
    const payments = {
      MXN: {
        Succeeded: { count: 2, amount: '320.00', net_amount: '300.80' },
        Failed: { count: 1, amount: '89.90', net_amount: '86.87' },
        Voided: { count: 1, amount: '50.00' },
        PreAuthorized: { count: 1, amount: '500.00' },
      },
    };
    return of<PaymentSummaryResponse>({
      source: 'mock',
      links: this.buildLinksSection(params.id_company),
      payments: this.filterPaymentsSectionByKind(payments, params.kind),
    }).pipe(delay(MOCK_DELAY_MS));
  }

  /**
   * Sección `links` de `/summary/` derivada de `MOCK_LINKS` (payments_links_paginacion_y_frescura
   * §4.2): sin esto las tarjetas de Links de Pago salían en cero con mocks. Como en el
   * backend, NO excluye los eliminados y `kind` no la acota. `id_company` filtra por
   * igualdad estricta, igual que `listLinks`; sin él, todos.
   */
  private buildLinksSection(idCompany: PaymentSummaryQueryParams['id_company']): PaymentSummaryResponse['links'] {
    const filtra = idCompany !== undefined && idCompany !== null && idCompany !== '';
    const porEstado = new Map<PaymentLinkStatus, { count: number; cents: number }>();
    for (const row of MOCK_LINKS) {
      if (filtra && row.id_company !== idCompany) continue;
      const acc = porEstado.get(row.status) ?? { count: 0, cents: 0 };
      acc.count += 1;
      acc.cents += Math.round(Number(row.amount) * 100);
      porEstado.set(row.status, acc);
    }
    if (porEstado.size === 0) return {};
    const mxn: Partial<Record<PaymentLinkStatus, SummaryBucket>> = {};
    for (const [status, acc] of porEstado) {
      mxn[status] = { count: acc.count, amount: (acc.cents / 100).toFixed(2) };
    }
    return { MXN: mxn };
  }

  getSummaryGlobal(
    params: Pick<PaymentSummaryQueryParams, 'kind' | 'date_from' | 'date_to'> = {},
  ): Observable<PaymentSummaryGlobalResponse> {
    // Datos fijos "sin filtro" (kind ausente): en el backend real, éste es el
    // agregado del ledger. En el mock simulamos la regla del contrato §Summary/
    // Filtros — el `kind` acota SOLO la sección `payments`; la de `links` NO
    // se filtra JAMÁS, porque cuenta links emitidos (otra dimensión).
    //
    // Además de `Succeeded` vienen renglones que NO son cobro (R15), y el `Failed`
    // trae `net_amount` positivo, como lo manda el gateway (§Huecos 5): la pantalla
    // NO lo puede sumar. `PreAuthorized` solo sale con `kind=PREAUTH`.
    const comercio1Payments = {
      MXN: {
        Pending: { count: 1, amount: '45.00' },
        PreAuthorized: { count: 1, amount: '500.00' },
        Succeeded: { count: 2, amount: '320.00', net_amount: '300.80' },
        Refunded: { count: 1, amount: '60.00' },
        Reversed: { count: 1, amount: '25.00' },
      },
    };
    const comercio2Payments = {
      MXN: {
        Failed: { count: 1, amount: '89.90', net_amount: '86.87' },
        Voided: { count: 1, amount: '50.00' },
      },
    };
    const totalPayments = {
      MXN: {
        Pending: { count: 1, amount: '45.00' },
        PreAuthorized: { count: 1, amount: '500.00' },
        Succeeded: { count: 2, amount: '320.00', net_amount: '300.80' },
        Failed: { count: 1, amount: '89.90', net_amount: '86.87' },
        Refunded: { count: 1, amount: '60.00' },
        Voided: { count: 1, amount: '50.00' },
        Reversed: { count: 1, amount: '25.00' },
      },
    };

    const comercio1: PaymentCompanySummary = {
      id_company: 1,
      company_name: 'Tienda Demo SA',
      links: { MXN: { Completed: { count: 3, amount: '450.00' } } },
      payments: this.filterPaymentsSectionByKind(comercio1Payments, params.kind),
    };
    const comercio2: PaymentCompanySummary = {
      id_company: 2,
      company_name: 'Franquicia Norte',
      links: { MXN: { Pending: { count: 1, amount: '75.00' } } },
      payments: this.filterPaymentsSectionByKind(comercio2Payments, params.kind),
    };
    return of<PaymentSummaryGlobalResponse>({
      source: 'mock',
      total: {
        links: { MXN: { Completed: { count: 3, amount: '450.00' }, Pending: { count: 1, amount: '75.00' } } },
        payments: this.filterPaymentsSectionByKind(totalPayments, params.kind),
      },
      por_comercio: [comercio1, comercio2],
    }).pipe(delay(MOCK_DELAY_MS));
  }

  /**
   * Simula la regla del backend: en prod el ledger filtra `payments` por
   * `kind`; acá aproximamos escalando los buckets para que el usuario vea la
   * pantalla cambiar al alternar chips.
   * - `undefined` → la sección sin el renglón `PreAuthorized` (el default del
   *   backend excluye PREAUTH: una retención viva no es ingreso).
   * - `'CHARGE'`  → 60% de los buckets (subset conservador), sin `PreAuthorized`.
   * - `'LINK'`    → 40% de los buckets (subset complementario), sin `PreAuthorized`.
   * - `'PREAUTH'` → SOLO el renglón `PreAuthorized` ("cuánto tengo retenido").
   * - `'QR'`      → todo a 0 (el mock no agrega pagos QR al resumen).
   * La suma `CHARGE + LINK` no calza con el default a propósito: en prod es el
   * ledger real quien fija los números; el mock solo tiene que ser distinguible.
   */
  private filterPaymentsSectionByKind(
    section: Record<string, Record<string, SummaryBucket> | undefined>,
    kind?: PaymentSummaryQueryParams['kind'],
  ): Record<string, Record<string, SummaryBucket> | undefined> {
    const preauthOnly = kind === 'PREAUTH';
    const factor = !kind ? 1 : kind === 'CHARGE' ? 0.6 : kind === 'LINK' ? 0.4 : 0;
    const out: Record<string, Record<string, SummaryBucket> | undefined> = {};
    for (const [currency, buckets] of Object.entries(section)) {
      if (!buckets) continue;
      const scaled: Record<string, SummaryBucket> = {};
      for (const [status, bucket] of Object.entries(buckets)) {
        if (!bucket) continue;
        if ((status === 'PreAuthorized') !== preauthOnly) continue;
        if (preauthOnly || factor === 1) {
          scaled[status] = bucket;
          continue;
        }
        scaled[status] = {
          count: Math.round(bucket.count * factor),
          amount: (Number(bucket.amount) * factor).toFixed(2),
          ...(bucket.net_amount !== undefined && bucket.net_amount !== null
            ? { net_amount: (Number(bucket.net_amount) * factor).toFixed(2) }
            : {}),
        };
      }
      out[currency] = scaled;
    }
    return out;
  }

  getEncryptionKey(): Observable<EncryptionKeyResponse> {
    return of<EncryptionKeyResponse>({ publicKey: '' }).pipe(delay(MOCK_DELAY_MS));
  }

  listCharges(params: PaymentChargeListParams = {}): Observable<PaymentChargeListResponse> {
    // Filas deliberadamente desparejas: una CON correo, una SIN (los pagos por link
    // nunca lo traen) y una fallida, que es lo que la pantalla tiene que aguantar.
    const results: PaymentChargeListRow[] = [
      {
        id: 'mock-charge-1',
        idTransaction: 'mock-tx-1',
        kind: 'CHARGE',
        amount: 1250.5,
        fee: 36.3645,
        tax: 5.817120,
        netAmount: 1208.318380,
        currency: 'MXN',
        status: 'Succeeded',
        customerEmail: 'jose.munoz@ejemplo.com',
        description: 'Pedido #42',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-17T18:05:00Z',
        payerDisplayName: '',
        wallet: '',
      },
      {
        id: 'mock-charge-2',
        idTransaction: 'mock-tx-2',
        kind: 'LINK',
        amount: 320,
        fee: 9.28,
        tax: 1.4848,
        netAmount: 309.2352,
        currency: 'MXN',
        status: 'Succeeded',
        customerEmail: '',
        description: 'Link de cobro septiembre',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-17T12:40:00Z',
        payerDisplayName: '',
        wallet: '',
      },
      {
        id: 'mock-charge-3',
        idTransaction: 'mock-tx-3',
        kind: 'CHARGE',
        amount: 89.9,
        fee: 2.6071,
        tax: 0.417136,
        netAmount: 86.875864,
        currency: 'MXN',
        status: 'Failed',
        customerEmail: 'ana@ejemplo.com',
        description: '',
        failureReason: 'do_not_honor',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-16T09:15:00Z',
        payerDisplayName: '',
        wallet: '',
      },
      // Pagos de cobro QR (kind QR): traen pagador YA enmascarado y wallet, nunca correo.
      {
        id: 'mock-charge-qr-1',
        idTransaction: 'mock-tx-qr-1',
        kind: 'QR',
        amount: 150,
        fee: 1.16,
        tax: 0.1856,
        netAmount: 148.6544,
        currency: 'MXN',
        status: 'Succeeded',
        customerEmail: '',
        description: 'Mesa 7',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-18T19:02:00Z',
        payerDisplayName: 'Juan P.',
        wallet: 'LIBERATE',
      },
      {
        id: 'mock-charge-qr-2',
        idTransaction: 'mock-tx-qr-2',
        kind: 'QR',
        amount: 420,
        fee: null,
        tax: null,
        netAmount: null,
        currency: 'MXN',
        status: 'Pending',
        customerEmail: '',
        description: 'Ticket 4819',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-18T19:10:00Z',
        payerDisplayName: 'Ana G.',
        wallet: 'LIBERATE',
      },
      {
        id: 'mock-charge-qr-3',
        idTransaction: 'mock-tx-qr-3',
        kind: 'QR',
        amount: 75,
        fee: null,
        tax: null,
        netAmount: null,
        currency: 'MXN',
        status: 'Failed',
        customerEmail: '',
        description: 'Ticket 4820',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-18T19:20:00Z',
        payerDisplayName: 'Luis M.',
        wallet: '',
      },
      // Estados y tipos que el resto de las filas no cubre (R15): con ellas están
      // los siete estados y los cuatro tipos del contrato. Datos de demostración.
      {
        id: 'mock-charge-preauth-1',
        idTransaction: 'mock-tx-preauth-1',
        kind: 'PREAUTH',
        amount: 500,
        fee: null,
        tax: null,
        netAmount: null,
        currency: 'MXN',
        status: 'PreAuthorized',
        customerEmail: 'hotel@ejemplo.com',
        description: 'Garantía de reserva',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-19T08:30:00Z',
        payerDisplayName: '',
        wallet: '',
      },
      {
        id: 'mock-charge-refund-1',
        idTransaction: 'mock-tx-refund-1',
        kind: 'CHARGE',
        amount: 60,
        fee: 1.74,
        tax: 0.2784,
        netAmount: 57.9816,
        currency: 'MXN',
        status: 'Refunded',
        customerEmail: 'luis@ejemplo.com',
        description: 'Pedido #38 devuelto',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-15T16:45:00Z',
        payerDisplayName: '',
        wallet: '',
      },
      {
        // Como el gateway: un cargo cancelado conserva fee/tax/neto positivos.
        id: 'mock-charge-void-1',
        idTransaction: 'mock-tx-void-1',
        kind: 'CHARGE',
        amount: 50,
        fee: 1.45,
        tax: 0.232,
        netAmount: 48.318,
        currency: 'MXN',
        status: 'Voided',
        customerEmail: 'marta@ejemplo.com',
        description: 'Pedido #44 cancelado',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-19T10:05:00Z',
        payerDisplayName: '',
        wallet: '',
      },
      {
        id: 'mock-charge-reverse-1',
        idTransaction: 'mock-tx-reverse-1',
        kind: 'LINK',
        amount: 25,
        fee: 0.725,
        tax: 0.116,
        netAmount: 24.159,
        currency: 'MXN',
        status: 'Reversed',
        customerEmail: '',
        description: 'Link de cobro agosto',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-14T11:20:00Z',
        payerDisplayName: '',
        wallet: '',
      },
      {
        id: 'mock-charge-link-pending-1',
        idTransaction: 'mock-tx-link-pending-1',
        kind: 'LINK',
        amount: 45,
        fee: null,
        tax: null,
        netAmount: null,
        currency: 'MXN',
        status: 'Pending',
        customerEmail: '',
        description: 'Link de cobro octubre',
        failureReason: '',
        idCompany: params.id_company ?? 1,
        companyName: 'Comercio Demo SA',
        createdAt: '2026-09-19T12:00:00Z',
        payerDisplayName: '',
        wallet: '',
      },
    ];
    const email = (params.customer_email ?? '').trim().toLowerCase();
    // El backend NO distingue mayúsculas; el mock respeta esa regla o probaría otra cosa.
    let filtered = email
      ? results.filter((r) => r.customerEmail.toLowerCase().includes(email))
      : results;

    // Filtro de kind (CHARGE/LINK/PREAUTH/QR) — igualdad estricta, como el back.
    if (params.kind) {
      filtered = filtered.filter((r) => r.kind === params.kind);
    }

    // Filtro de status — igualdad estricta (contrato §2 2026-09-22, R11).
    if (params.status) {
      filtered = filtered.filter((r) => r.status === params.status);
    }

    // Filtro de search — substring case-insensitive contra id, customerEmail y companyName.
    // NO contra description (contrato §2 2026-09-22, R12).
    if (params.search && params.search.trim()) {
      const term = params.search.trim().toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.id.toLowerCase().includes(term) ||
          r.customerEmail.toLowerCase().includes(term) ||
          r.companyName.toLowerCase().includes(term),
      );
    }

    // date_from, date_to, min_amount, max_amount y offset se ignoran silenciosamente
    // (datos fijos no filtran por rango; R13).

    return of<PaymentChargeListResponse>({ count: filtered.length, results: filtered }).pipe(
      delay(MOCK_DELAY_MS),
    );
  }

  createCharge(input: PaymentChargeCreateRequest): Observable<PaymentChargeResponse> {
    const amount = Number(input.amount) || 0;
    return of<PaymentChargeResponse>({
      id: 'mock-charge-uuid',
      id_transaction: 'mock-tx-uuid',
      amount,
      fee: 0,
      tax: 0,
      netAmount: amount,
      currency: 'MXN',
      status: 'Succeeded',
      failureReason: null,
      requires3ds: false,
      redirectUrl: null,
      createdAt: '1970-01-01T00:00:00Z',
    }).pipe(delay(MOCK_DELAY_MS));
  }

  verify3ds(): Observable<PaymentChargeResponse> {
    return of<PaymentChargeResponse>({
      id: 'mock-charge-uuid',
      id_transaction: 'mock-tx-uuid',
      amount: 0,
      fee: 0,
      tax: 0,
      netAmount: 0,
      currency: 'MXN',
      status: 'Succeeded',
      failureReason: null,
      requires3ds: false,
      redirectUrl: null,
      createdAt: '1970-01-01T00:00:00Z',
    }).pipe(delay(MOCK_DELAY_MS));
  }

  // ── Comercios (§4 sub-sección) ──────────────────────────────────

  /**
   * Alta con los mismos desenlaces que el back y en su orden (TOTP primero):
   * `''` → 400 MISSING_TOTP · `'000000'` → 401 INVALID_TOTP · `'999999'` → 403
   * TOTP_VERIFICATION_ERROR · sin destino → 400 PAYMENTS_DESTINATION_REQUIRED ·
   * `id_company` ya dado de alta → 409 · resto → 201 con la forma del listado.
   * No muta `MOCK_COMPANIES` (los specs de listado dependen de su contenido).
   */
  createCompany(input: PaymentCompanyCreateRequest): Observable<PaymentCompany> {
    const totp = input.totp_code ?? '';
    if (!totp) {
      return mockHttpError(400, { error: 'mock', detail: 'totp_required', error_code: 'MISSING_TOTP' });
    }
    if (totp === MOCK_TOTP_INVALIDO) {
      return mockHttpError(401, { error: 'mock', detail: 'invalid_totp_code', error_code: 'INVALID_TOTP' });
    }
    if (totp === MOCK_TOTP_SIN_ENROLAR) {
      return mockHttpError(403, {
        error: 'mock',
        detail: 'totp_validation_failed',
        error_code: 'TOTP_VERIFICATION_ERROR',
      });
    }
    if (!input.merchant_id && !input.gateway_account_id) {
      return mockHttpError(400, { detail: 'mock', error_code: 'PAYMENTS_DESTINATION_REQUIRED' });
    }
    const idCompany = input.id_company;
    if (idCompany !== undefined && MOCK_COMPANIES.some((c) => c.org_company_id === idCompany)) {
      return mockHttpError(409, { detail: 'mock' });
    }

    const siguientePk = MOCK_COMPANIES.reduce((max, c) => Math.max(max, Number(c.id_company) || 0), 0) + 1;
    return of<PaymentCompany>({
      id_company: siguientePk,
      company_name: input.name ?? `Comercio ${String(idCompany)}`,
      org_company_id: idCompany ?? null,
      external_ref: MOCK_EXTERNAL_REF_ALTA,
      cobra_a: input.gateway_account_id ? 'cuenta propia' : 'comercio Memphis',
      gateway_account_id: input.gateway_account_id ?? null,
      is_active: true,
    }).pipe(delay(MOCK_DELAY_MS));
  }

  /**
   * Filtra server-side sobre `MOCK_COMPANIES` en el mismo orden que el remote:
   * `search` (substring case-insensitive contra nombre y razón social),
   * `is_active` (igualdad estricta), `cobra_a` (igualdad exacta case-sensitive —
   * el backend distingue). Después pagina cortando `slice(offset, offset+limit)`
   * si `limit` está presente. `count` = total ANTES de paginar.
   */
  listCompanies(params: PaymentCompanyListParams = {}): Observable<PaymentCompanyListResponse> {
    let filtered = MOCK_COMPANIES.slice();

    const search = (params.search ?? '').trim().toLowerCase();
    if (search) {
      filtered = filtered.filter(
        (c) =>
          c.company_name.toLowerCase().includes(search) ||
          (c.legal_name ?? '').toLowerCase().includes(search),
      );
    }

    if (typeof params.is_active === 'boolean') {
      filtered = filtered.filter((c) => c.is_active === params.is_active);
    }

    if (params.cobra_a && params.cobra_a.trim()) {
      const target = params.cobra_a.trim();
      filtered = filtered.filter((c) => c.cobra_a === target);
    }

    const total = filtered.length;

    if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
      const limit = Math.max(1, Math.trunc(params.limit));
      const offset =
        typeof params.offset === 'number' && Number.isFinite(params.offset) && params.offset > 0
          ? Math.trunc(params.offset)
          : 0;
      filtered = filtered.slice(offset, offset + limit);
    }

    return of<PaymentCompanyListResponse>({ count: total, results: filtered }).pipe(
      delay(MOCK_DELAY_MS),
    );
  }
}
